// SCR-STAFF-872 (AP-INV-02؛ US-INV-010 تا 015، 024): اسناد انبار شماره‌دار — رسید با تأمین‌کننده، سفارش، بچ، انقضا، بهای واحد و تصویر فاکتور و تأیید
// Manager برای انقضای نزدیک؛ حواله با مرکز هزینه و دریافت‌کننده؛ انتقال یک‌مرحله‌ای یا در راه و دریافت با مغایرت؛ مرجوعی با ارجاع به رسید؛ ضایعات با علت
// و تأیید بیش از سقف؛ صدور قطعه برای دستور کار؛ برگشت فروش منتظر (سالم ← موجودی، ناسالم ← ضایعات).
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  unwrap,
  type InvDocumentLineRequest,
  type InvDocumentView,
  type InvItemUnit,
  type InvItemView,
  type InvSalesReturnView,
  type InvStockDocumentStatus,
  type InvStockDocumentType,
  type InvWasteReason,
} from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, faDigits, FormDialog, JalaliDate, ListPage, Money, StatusChip, type DataTableColumn } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { parseJalaliInput } from '../patients/common';
import {
  CREATABLE_DOCUMENTS,
  DOCUMENT_STATUSES,
  DOCUMENT_TYPES,
  documentTone,
  fromMilli,
  stockKeys,
  toMilli,
  useItemOptions,
  useQuantityText,
  useWarehouses,
  WarehouseSelect,
  warehouseName,
  WASTE_REASONS,
} from './stockCommon';

export const SCR_STAFF_872 = 'SCR-STAFF-872';

const CREATE_PERMISSION: Record<InvStockDocumentType, string> = {
  Receipt: 'inv.receipt.manage',
  Issue: 'inv.issue.manage',
  Transfer: 'inv.transfer.manage',
  ReturnToSupplier: 'inv.receipt.manage',
  Waste: 'inv.waste.create',
  MaintenanceIssue: 'inv.issue.manage',
  OpeningBalance: 'inv.receipt.manage',
  StocktakeAdjustment: 'inv.stocktake.approve',
  SalesReturn: 'inv.receipt.manage',
};

export function StockDocumentsPage() {
  const { t } = useTranslation('inv');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const warehouses = useWarehouses();
  const quantity = useQuantityText();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<'documents' | 'returns'>('documents');
  const [type, setType] = useState<InvStockDocumentType | ''>('');
  const [status, setStatus] = useState<InvStockDocumentStatus | ''>('');
  const [menu, setMenu] = useState<HTMLElement | null>(null);
  const [creating, setCreating] = useState<InvStockDocumentType | null>(null);
  const [receiving, setReceiving] = useState<InvDocumentView | null>(null);
  const full = access.accessMode === 'Full';

  const documents = useQuery({
    queryKey: [...stockKeys.documents(session?.tenantId), type, status],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/inventory/documents', { params: { query: { type: type || undefined, status: status || undefined, limit: 50 } } })),
  });
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: stockKeys.documents(session?.tenantId) });
    void queryClient.invalidateQueries({ queryKey: ['inventory', 'stock'] });
  };
  const decide = useMutation({
    mutationFn: ({ document, verb }: { document: InvDocumentView; verb: 'approve' | 'reject' }) => {
      const path = document.type === 'Waste' ? (verb === 'approve' ? '/api/v1/inventory/wastes/{id}:approve' : '/api/v1/inventory/wastes/{id}:reject') : verb === 'approve' ? '/api/v1/inventory/receipts/{id}:approve' : '/api/v1/inventory/receipts/{id}:reject';
      return unwrap(tenantApi.POST(path, { params: { path: { id: document.id } }, body: { reason: null } }));
    },
    onSuccess: refresh,
  });
  const approvePermission = (document: InvDocumentView) => (document.type === 'Waste' ? 'inv.waste.approve' : 'inv.receipt.approve');

  const columns: DataTableColumn<InvDocumentView>[] = [
    { id: 'number', headerKey: 'inv:documents.columns.number', cell: (row) => <bdi dir="ltr" data-testid={`document-${row.number}`}>{row.number}</bdi> },
    { id: 'type', headerKey: 'inv:documents.columns.type', cell: (row) => t(`inv:document_type.${row.type}`) },
    { id: 'status', headerKey: 'inv:documents.columns.status', cell: (row) => <StatusChip tone={documentTone(row.status)} labelKey={`inv:document_status.${row.status}`} /> },
    {
      id: 'warehouse',
      headerKey: 'inv:documents.columns.warehouse',
      cell: (row) => (row.toWarehouseId ? `${warehouseName(warehouses.data?.items, row.warehouseId)} ← ${warehouseName(warehouses.data?.items, row.toWarehouseId)}` : warehouseName(warehouses.data?.items, row.warehouseId)),
    },
    {
      id: 'lines',
      headerKey: 'inv:documents.columns.lines',
      cell: (row) => row.lines.map((line) => `${line.itemName}${line.batchNumber ? ` (${line.batchNumber})` : ''}: ${quantity(line.quantityMilli, line.baseUnit)}`).join(t('common:list_separator')),
    },
    { id: 'total', headerKey: 'inv:documents.columns.total', cell: (row) => <Money amountMinor={row.totalCost.amount} currency={row.totalCost.currency} /> },
    { id: 'date', headerKey: 'inv:documents.columns.date', cell: (row) => <JalaliDate value={row.businessDate} /> },
    {
      id: 'actions',
      headerKey: 'inv:documents.columns.actions',
      cell: (row) => (
        <Stack direction="row" spacing={0.5}>
          {row.warnings.map((warning) => (
            <Typography key={warning} variant="caption" color="warning.main">
              {t(`inv:documents.warnings.${warning}`)}
            </Typography>
          ))}
          {row.status === 'PendingApproval' && (
            <>
              <Button size="small" disabled={!full || !access.can(approvePermission(row)) || decide.isPending} onClick={() => decide.mutate({ document: row, verb: 'approve' })} data-testid={`document-approve-${row.number}`}>
                {t('inv:documents.approve')}
              </Button>
              <Button size="small" color="error" disabled={!full || !access.can(approvePermission(row)) || decide.isPending} onClick={() => decide.mutate({ document: row, verb: 'reject' })}>
                {t('inv:documents.reject')}
              </Button>
            </>
          )}
          {row.status === 'InTransit' && (
            <Button size="small" disabled={!full || !access.can('inv.transfer.manage')} onClick={() => setReceiving(row)} data-testid={`document-receive-${row.number}`}>
              {t('inv:documents.receive')}
            </Button>
          )}
        </Stack>
      ),
    },
  ];

  return (
    <ListPage
      screenId={SCR_STAFF_872}
      titleKey="inv:documents.title"
      subtitleKey="inv:documents.subtitle"
      actions={
        <>
          <Button variant="contained" startIcon={<AddIcon />} disabled={!full || !CREATABLE_DOCUMENTS.some((value) => access.can(CREATE_PERMISSION[value]))} onClick={(event) => setMenu(event.currentTarget)} data-testid="document-create">
            {t('inv:documents.add')}
          </Button>
          <Menu anchorEl={menu} open={Boolean(menu)} onClose={() => setMenu(null)}>
            {CREATABLE_DOCUMENTS.filter((value) => access.can(CREATE_PERMISSION[value])).map((value) => (
              <MenuItem
                key={value}
                onClick={() => {
                  setMenu(null);
                  setCreating(value);
                }}
                data-testid={`document-create-${value}`}
              >
                {t(`inv:document_type.${value}`)}
              </MenuItem>
            ))}
          </Menu>
        </>
      }
      filters={
        <Stack spacing={1}>
          <Tabs value={tab} onChange={(_event, value: 'documents' | 'returns') => setTab(value)} aria-label={t('inv:documents.title')}>
            <Tab value="documents" label={t('inv:documents.tabs.documents')} />
            <Tab value="returns" label={t('inv:documents.tabs.returns')} data-testid="documents-tab-returns" />
          </Tabs>
          {tab === 'documents' && (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField select size="small" label={t('inv:documents.columns.type')} value={type} onChange={(event) => setType(event.target.value as InvStockDocumentType | '')} sx={{ minWidth: 200 }}>
                <MenuItem value="">{t('inv:items.filters.all')}</MenuItem>
                {DOCUMENT_TYPES.map((value) => (
                  <MenuItem key={value} value={value}>
                    {t(`inv:document_type.${value}`)}
                  </MenuItem>
                ))}
              </TextField>
              <TextField select size="small" label={t('inv:documents.columns.status')} value={status} onChange={(event) => setStatus(event.target.value as InvStockDocumentStatus | '')} sx={{ minWidth: 200 }}>
                <MenuItem value="">{t('inv:items.filters.all')}</MenuItem>
                {DOCUMENT_STATUSES.map((value) => (
                  <MenuItem key={value} value={value}>
                    {t(`inv:document_status.${value}`)}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          )}
        </Stack>
      }
    >
      {tab === 'documents' ? (
        <DataTable
          captionKey="inv:documents.caption"
          columns={columns}
          rows={documents.data?.items}
          getRowId={(row) => row.id}
          isLoading={documents.isPending}
          error={documents.error}
          onRetry={() => void documents.refetch()}
          emptyTitleKey="inv:documents.empty"
        />
      ) : (
        <SalesReturnsTable />
      )}
      {creating && (
        <DocumentDialog
          type={creating}
          onClose={() => setCreating(null)}
          onSaved={() => {
            setCreating(null);
            refresh();
          }}
        />
      )}
      {receiving && (
        <ReceiveTransferDialog
          document={receiving}
          onClose={() => setReceiving(null)}
          onSaved={() => {
            setReceiving(null);
            refresh();
          }}
        />
      )}
    </ListPage>
  );
}

interface LineState {
  itemId: string;
  quantity: string;
  unit: InvItemUnit | '';
  batchId: string;
  batchNumber: string;
  expiryDate: string;
  unitCost: string;
  /** شناسه‌های رهگیری اسکن‌شده کالای مشمول، هر خط یک شناسه (AP-INT-04؛ US-INV-080). */
  trackingUids: string;
}

const emptyLine: LineState = { itemId: '', quantity: '', unit: '', batchId: '', batchNumber: '', expiryDate: '', unitCost: '', trackingUids: '' };

/** هر خط ورودی یک شناسه رهگیری؛ خط تهی و فاصله دور افتاده کنار می‌رود. */
function parseUids(text: string): string[] {
  return text
    .split(/[\n,،]/)
    .map((value) => value.trim())
    .filter((value) => value.length > 0);
}

function unitsOf(item: InvItemView | undefined): InvItemUnit[] {
  if (!item) {
    return [];
  }
  return [...new Set<InvItemUnit>([item.baseUnit, item.purchaseUnit, item.consumptionUnit, ...item.conversions.map((conversion) => conversion.unit)])];
}

function DocumentDialog({ type, onClose, onSaved }: { type: InvStockDocumentType; onClose: () => void; onSaved: () => void }) {
  const { t } = useTranslation('inv');
  const { tenantApi } = useTenantSession();
  const idempotency = useIdempotencyKey();
  const write = useWriteAccess(CREATE_PERMISSION[type]);
  const items = useItemOptions();
  const [warehouseId, setWarehouseId] = useState('');
  const [toWarehouseId, setToWarehouseId] = useState('');
  const [supplierId, setSupplierId] = useState('');
  const [purchaseOrderRef, setPurchaseOrderRef] = useState('');
  const [costCenter, setCostCenter] = useState('');
  const [recipient, setRecipient] = useState('');
  const [reason, setReason] = useState<NonNullable<InvWasteReason>>('Damaged');
  const [receiptId, setReceiptId] = useState('');
  const [returnReason, setReturnReason] = useState('');
  const [workOrderId, setWorkOrderId] = useState('');
  const [note, setNote] = useState('');
  const [lines, setLines] = useState<LineState[]>([{ ...emptyLine }]);
  const [submitError, setSubmitError] = useState<unknown>();
  const receipts = useQuery({
    queryKey: ['inventory', 'documents', 'receipt-options'],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/inventory/documents', { params: { query: { type: 'Receipt', status: 'Posted', limit: 50 } } })),
    enabled: type === 'ReturnToSupplier',
  });
  const suppliers = useQuery({
    queryKey: ['inventory', 'suppliers', 'document-options'],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/inventory/suppliers', { params: { query: { active: true, limit: 100 } } })),
    enabled: type === 'Receipt',
  });

  const itemOf = (id: string) => items.data?.items.find((item) => item.id === id);
  const update = (index: number, patch: Partial<LineState>) => setLines((current) => current.map((line, i) => (i === index ? { ...line, ...patch } : line)));
  const toLine = (line: LineState): InvDocumentLineRequest => ({
    itemId: line.itemId,
    quantityMilli: toMilli(line.quantity) ?? 0,
    unit: line.unit || null,
    batchId: line.batchId || null,
    batchNumber: line.batchNumber.trim() || null,
    expiryDate: parseJalaliInput(line.expiryDate) ?? null,
    manufactureDate: null,
    unitCost: type === 'Receipt' && line.unitCost.trim() ? { amount: Math.round((toMilli(line.unitCost) ?? 0) / 1000), currency: 'IRR' } : undefined,
    // کالای مشمول ردیابی فقط در رسید شناسه می‌گیرد؛ کالای نامشمول با شناسه رد می‌شود (PHX-INV-0115).
    trackingUids: type === 'Receipt' && itemOf(line.itemId)?.tracksUid ? parseUids(line.trackingUids) : undefined,
  });

  const save = useMutation({
    mutationFn: () => {
      const body = lines.filter((line) => line.itemId).map(toLine);
      const headers = { 'Idempotency-Key': idempotency.current() };
      switch (type) {
        case 'Receipt':
          return unwrap(tenantApi.POST('/api/v1/inventory/receipts', { body: { warehouseId, supplierId: supplierId || null, purchaseOrderRef: purchaseOrderRef.trim() || null, invoiceFileId: null, note: note.trim() || null, lines: body }, headers }));
        case 'Issue':
          return unwrap(tenantApi.POST('/api/v1/inventory/issues', { body: { warehouseId, costCenter: costCenter.trim() || null, recipient: recipient.trim() || null, note: note.trim() || null, lines: body }, headers }));
        case 'Transfer':
          return unwrap(tenantApi.POST('/api/v1/inventory/transfers', { body: { fromWarehouseId: warehouseId, toWarehouseId, note: note.trim() || null, lines: body }, headers }));
        case 'ReturnToSupplier':
          return unwrap(tenantApi.POST('/api/v1/inventory/supplier-returns', { body: { receiptId, reason: returnReason.trim(), lines: body }, headers }));
        case 'Waste':
          return unwrap(tenantApi.POST('/api/v1/inventory/wastes', { body: { warehouseId, reason, note: note.trim() || null, lines: body }, headers }));
        default:
          return unwrap(tenantApi.POST('/api/v1/inventory/maintenance-issues', { body: { warehouseId, workOrderId: workOrderId.trim(), deviceId: null, note: note.trim() || null, lines: body }, headers }));
      }
    },
    onMutate: () => setSubmitError(undefined),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: onSaved,
    onError: setSubmitError,
  });

  return (
    <FormDialog
      open
      screenId="SCR-STAFF-872-DOCUMENT-FORM"
      titleKey={`inv:documents.create_title.${type}`}
      onSubmit={() => (write.canWrite ? save.mutate() : undefined)}
      onClose={onClose}
      isDirty={lines.some((line) => line.itemId)}
      isSubmitting={save.isPending}
      submitError={submitError}
      maxWidth="lg"
    >
      {type !== 'ReturnToSupplier' && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <WarehouseSelect value={warehouseId} onChange={setWarehouseId} labelKey={type === 'Transfer' ? 'inv:documents.from_warehouse' : 'inv:stock.warehouse'} testId="document-warehouse" required />
          {type === 'Transfer' && <WarehouseSelect value={toWarehouseId} onChange={setToWarehouseId} labelKey="inv:documents.to_warehouse" testId="document-to-warehouse" required />}
        </Stack>
      )}
      {type === 'Receipt' && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField select size="small" label={t('inv:documents.supplier')} value={supplierId} onChange={(event) => setSupplierId(event.target.value)} sx={{ minWidth: 240 }}>
            <MenuItem value="">{t('inv:documents.no_supplier')}</MenuItem>
            {(suppliers.data?.items ?? []).map((supplier) => (
              <MenuItem key={supplier.id} value={supplier.id}>
                {supplier.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField size="small" label={t('inv:documents.purchase_order')} value={purchaseOrderRef} onChange={(event) => setPurchaseOrderRef(event.target.value)} slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'document-po' } }} />
        </Stack>
      )}
      {type === 'Issue' && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField size="small" label={t('inv:documents.cost_center')} value={costCenter} onChange={(event) => setCostCenter(event.target.value)} slotProps={{ htmlInput: { 'data-testid': 'document-cost-center' } }} />
          <TextField size="small" label={t('inv:documents.recipient')} value={recipient} onChange={(event) => setRecipient(event.target.value)} />
        </Stack>
      )}
      {type === 'Waste' && (
        <TextField select size="small" label={t('inv:documents.waste_reason')} value={reason} onChange={(event) => setReason(event.target.value as NonNullable<InvWasteReason>)} slotProps={{ htmlInput: { 'data-testid': 'document-waste-reason' } }}>
          {WASTE_REASONS.map((value) => (
            <MenuItem key={value} value={value}>
              {t(`inv:waste_reason.${value}`)}
            </MenuItem>
          ))}
        </TextField>
      )}
      {type === 'ReturnToSupplier' && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField select size="small" required label={t('inv:documents.receipt')} value={receiptId} onChange={(event) => setReceiptId(event.target.value)} sx={{ minWidth: 240 }}>
            {(receipts.data?.items ?? []).map((receipt) => (
              <MenuItem key={receipt.id} value={receipt.id}>
                {receipt.number}
              </MenuItem>
            ))}
          </TextField>
          <TextField size="small" required label={t('inv:documents.return_reason')} value={returnReason} onChange={(event) => setReturnReason(event.target.value)} />
        </Stack>
      )}
      {type === 'MaintenanceIssue' && (
        <TextField size="small" required label={t('inv:documents.work_order')} value={workOrderId} onChange={(event) => setWorkOrderId(event.target.value)} slotProps={{ htmlInput: { dir: 'ltr' } }} />
      )}
      {type === 'Waste' && <Alert severity="info">{t('inv:documents.waste_limit_note')}</Alert>}
      <Typography variant="subtitle2" component="h3">
        {t('inv:documents.lines')}
      </Typography>
      {lines.map((line, index) => {
        const item = itemOf(line.itemId);
        return (
          <Stack key={index} direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }} data-testid="document-line">
            <TextField select size="small" required label={t('inv:stock.columns.item')} value={line.itemId} onChange={(event) => update(index, { itemId: event.target.value, unit: '', batchId: '' })} sx={{ minWidth: 220 }} slotProps={{ htmlInput: { 'data-testid': `document-line-item-${index}` } }}>
              {(items.data?.items ?? []).map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField size="small" required label={t('inv:documents.quantity')} value={line.quantity} onChange={(event) => update(index, { quantity: event.target.value })} sx={{ width: 120 }} slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'decimal', 'data-testid': `document-line-quantity-${index}` } }} />
            <TextField select size="small" label={t('inv:documents.unit')} value={line.unit || item?.baseUnit || ''} onChange={(event) => update(index, { unit: event.target.value as InvItemUnit })} sx={{ width: 140 }} disabled={!item}>
              {unitsOf(item).map((unit) => (
                <MenuItem key={unit} value={unit}>
                  {t(`inv:unit.${unit}`)}
                </MenuItem>
              ))}
            </TextField>
            {item?.tracksBatch && type === 'Receipt' && (
              <>
                <TextField size="small" required label={t('inv:documents.batch_number')} value={line.batchNumber} onChange={(event) => update(index, { batchNumber: event.target.value })} slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': `document-line-batch-${index}` } }} />
                <TextField
                  size="small"
                  required={item.tracksExpiry}
                  label={t('inv:documents.expiry')}
                  placeholder="1406/03/10"
                  value={line.expiryDate}
                  onChange={(event) => update(index, { expiryDate: event.target.value })}
                  error={Boolean(line.expiryDate.trim()) && !parseJalaliInput(line.expiryDate)}
                  helperText={t('inv:documents.expiry_help')}
                  slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric', 'data-testid': `document-line-expiry-${index}` } }}
                />
              </>
            )}
            {item?.tracksBatch && type !== 'Receipt' && <BatchSelect itemId={item.id} value={line.batchId} onChange={(batchId) => update(index, { batchId })} index={index} />}
            {type === 'Receipt' && (
              <TextField size="small" label={t('inv:documents.unit_cost')} value={line.unitCost} onChange={(event) => update(index, { unitCost: event.target.value })} sx={{ width: 160 }} slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric', 'data-testid': `document-line-cost-${index}` } }} />
            )}
            {/* AP-INT-04 (AC-INV-080.1): شناسه رهگیری هر واحد کالای مشمول، در رسید الزامی است. */}
            {type === 'Receipt' && item?.tracksUid && (
              <TextField
                size="small"
                required
                multiline
                minRows={2}
                label={t('inv:tracking.scan')}
                helperText={t('inv:tracking.scan_help')}
                value={line.trackingUids}
                onChange={(event) => update(index, { trackingUids: event.target.value })}
                sx={{ minWidth: 220 }}
                slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': `document-line-uids-${index}` } }}
              />
            )}
            <IconButton aria-label={t('inv:documents.remove_line')} onClick={() => setLines((current) => current.filter((_line, i) => i !== index))} disabled={lines.length === 1}>
              <DeleteOutlineIcon />
            </IconButton>
          </Stack>
        );
      })}
      <Stack direction="row">
        <Button startIcon={<AddIcon />} onClick={() => setLines((current) => [...current, { ...emptyLine }])} data-testid="document-add-line">
          {t('inv:documents.add_line')}
        </Button>
      </Stack>
      {type !== 'ReturnToSupplier' && <TextField size="small" label={t('inv:documents.note')} value={note} onChange={(event) => setNote(event.target.value)} />}
    </FormDialog>
  );
}

/** بچ‌های قابل انتخاب کالا (FEFO؛ منقضی و فراخوان‌شده نه). */
function BatchSelect({ itemId, value, onChange, index }: { itemId: string; value: string; onChange: (id: string) => void; index: number }) {
  const { t } = useTranslation('inv');
  const { tenantApi, session } = useTenantSession();
  const batches = useQuery({
    queryKey: ['inventory', 'items', session?.tenantId, itemId, 'batches', true],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/inventory/items/{id}/batches', { params: { path: { id: itemId }, query: { activeOnly: true } } })),
  });
  return (
    <TextField select size="small" label={t('inv:documents.batch')} value={value} onChange={(event) => onChange(event.target.value)} sx={{ minWidth: 180 }} slotProps={{ htmlInput: { 'data-testid': `document-line-batch-select-${index}` } }}>
      <MenuItem value="">{t('inv:documents.fefo')}</MenuItem>
      {(batches.data?.items ?? []).map((batch) => (
        <MenuItem key={batch.id} value={batch.id}>
          {batch.batchNumber}
        </MenuItem>
      ))}
    </TextField>
  );
}

function ReceiveTransferDialog({ document, onClose, onSaved }: { document: InvDocumentView; onClose: () => void; onSaved: () => void }) {
  const { t } = useTranslation('inv');
  const { tenantApi } = useTenantSession();
  const idempotency = useIdempotencyKey();
  const quantity = useQuantityText();
  const [received, setReceived] = useState<Record<number, string>>(Object.fromEntries(document.lines.map((line) => [Number(line.lineNo), String(Number(line.quantityMilli) / 1000)])));
  const save = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/inventory/transfers/{id}:receive', {
          params: { path: { id: document.id } },
          body: { lines: document.lines.map((line) => ({ lineNo: Number(line.lineNo), receivedMilli: toMilli(received[Number(line.lineNo)] ?? '0') ?? 0 })) },
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: onSaved,
  });
  return (
    <FormDialog open screenId="SCR-STAFF-872-RECEIVE" titleKey="inv:documents.receive_title" onSubmit={() => save.mutate()} onClose={onClose} isDirty={false} isSubmitting={save.isPending} submitError={save.error}>
      {document.lines.map((line) => (
        <Stack key={line.lineNo} direction="row" spacing={2} alignItems="center">
          <span>
            {line.itemName} · {t('inv:documents.sent')}: {quantity(line.quantityMilli, line.baseUnit)}
          </span>
          <TextField
            size="small"
            label={t('inv:documents.received')}
            value={received[Number(line.lineNo)] ?? ''}
            onChange={(event) => setReceived((current) => ({ ...current, [Number(line.lineNo)]: event.target.value }))}
            slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'decimal', 'data-testid': `receive-line-${line.lineNo}` } }}
          />
        </Stack>
      ))}
    </FormDialog>
  );
}

function SalesReturnsTable() {
  const { t } = useTranslation('inv');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const full = access.accessMode === 'Full';
  const idempotency = useIdempotencyKey();
  const returns = useQuery({
    queryKey: stockKeys.salesReturns(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/inventory/sales-returns')),
  });
  const resolve = useMutation({
    mutationFn: ({ id, restock }: { id: string; restock: boolean }) =>
      unwrap(
        restock
          ? tenantApi.POST('/api/v1/inventory/sales-returns/{id}:restock', { params: { path: { id } }, headers: { 'Idempotency-Key': idempotency.current() } })
          : tenantApi.POST('/api/v1/inventory/sales-returns/{id}:waste', { params: { path: { id } }, headers: { 'Idempotency-Key': idempotency.current() } }),
      ),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: stockKeys.salesReturns(session?.tenantId) }),
  });
  const columns: DataTableColumn<InvSalesReturnView>[] = [
    { id: 'item', headerKey: 'inv:stock.columns.item', cell: (row) => `${row.itemName}${row.batchNumber ? ` (${row.batchNumber})` : ''}` },
    { id: 'quantity', headerKey: 'inv:documents.quantity', cell: (row) => faDigits(String(fromMilli(row.quantityMilli))) },
    { id: 'date', headerKey: 'inv:documents.columns.date', cell: (row) => <JalaliDate value={row.createdAt} withTime /> },
    {
      id: 'actions',
      headerKey: 'inv:documents.columns.actions',
      cell: (row) => (
        <Stack direction="row" spacing={0.5}>
          <Button size="small" disabled={!full || !access.can('inv.receipt.manage') || resolve.isPending} onClick={() => resolve.mutate({ id: row.id, restock: true })} data-testid={`return-restock-${row.id}`}>
            {t('inv:sales_returns.restock')}
          </Button>
          <Button size="small" color="error" disabled={!full || !access.can('inv.waste.create') || resolve.isPending} onClick={() => resolve.mutate({ id: row.id, restock: false })} data-testid={`return-waste-${row.id}`}>
            {t('inv:sales_returns.waste')}
          </Button>
        </Stack>
      ),
    },
  ];
  return <DataTable captionKey="inv:sales_returns.caption" columns={columns} rows={returns.data?.items} getRowId={(row) => row.id} isLoading={returns.isPending} error={returns.error} emptyTitleKey="inv:sales_returns.empty" />;
}

